import axios from "axios";
import React, { useState, useEffect } from "react";
import API_URL from "../../../../config";
import Sideform from "../../servicePage/Sideform";
import { useLocation, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { CustomNextArrow, CustomPrevArrow } from "../../../utility/CustomArrow";
import { motion } from "framer-motion";

const BlogDetails = () => {
  const location = useLocation();
  const blog = location.state?.blog;
  const [blogDetailsData, setBlogDetailsData] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const blogId = searchParams.get("blogId");

  const handleViewDetails = (blog) => {
    navigate("/blog-details", { state: { blog } });
  };

  // Fetch blog details by ID if blog data is not passed via state
  useEffect(() => {
    if (!blog && blogId) {
      fetchBlogDetails(blogId);
    } else if (blog?.id) {
      fetchBlogDetails(blog.id);
    } else {
      navigate("/");
    }
  }, [blog, blogId, navigate]);

  // Fetch blog details and related posts
  const fetchBlogDetails = (id) => {
    setLoading(true);
    axios
      .get(`${API_URL}/layout/blog/${id}`)
      .then((response) => {
        setBlogDetailsData(response?.data?.data || null);
        setRelatedPosts(response?.data?.relatedPost || []);
      })
      .catch((error) => {
        console.error("Error fetching blog details data:", error);
      })
      .finally(() => setLoading(false));
  };

  const settings = {
    dots: true,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1200,
        settings: { slidesToShow: 3, slidesToScroll: 1 },
      },
      {
        breakpoint: 768,
        settings: { slidesToShow: 2, slidesToScroll: 1 },
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 1, slidesToScroll: 1 },
      },
    ],
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!blogDetailsData || blogDetailsData.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-5"
      >
        <div className="alert alert-warning" role="alert">
          Blog post not found
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <title>{blogDetailsData[0]?.title}</title>
      <meta name="description" content={blogDetailsData[0]?.description} />

      <div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="blog-details">
          <div className="container">
            <div className="row">
              {/* Left Column - Blog Content */}
              <div className="col-lg-8 mb-5">
                <div
                  whileHover={{ scale: 1.01 }}
                  className="card shadow-lg p-4 rounded border-0"
                  style={{ backgroundColor: "#f8f9fa" }}
                >
                  {blogDetailsData?.map((blogData, index) => (
                    <div key={index}>
                      {/* Blog Title */}
                      <motion.h1
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="mb-4 text-dark fw-bold"
                        style={{ fontSize: "2.5rem", lineHeight: "1.2" }}
                      >
                        {blogData.title}
                      </motion.h1>

                      {/* Blog Meta */}
                      <motion.ul
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="cs_post_meta cs_mp0 mb-4 list-unstyled d-flex align-items-center gap-3 text-muted"
                      >
                        <li className="d-flex align-items-center">
                          <i className="fa-solid fa-user me-2"></i>
                          {blogData.author || "Admin"}
                        </li>
                        <li className="d-flex align-items-center">
                          <i className="fa-regular fa-calendar-days me-2"></i>
                          {new Date(blogData.start_date).toLocaleDateString(
                            "en-US",
                            {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            }
                          )}
                        </li>
                      </motion.ul>

                      {blogData.thumbnail && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.4 }}
                          className="mb-4 rounded overflow-hidden"
                        ></motion.div>
                      )}

                      {/* Blog Content */}
                      <motion.div
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="blog-content fs-5"
                        style={{ lineHeight: "1.8" }}
                        dangerouslySetInnerHTML={{
                          __html: blogData.content,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Sidebar */}
              <div className="col-lg-4">
                <motion.div
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="rightColumnStyle p-4 rounded shadow-sm"
                >
                  <Sideform />
                </motion.div>
              </div>
            </div>

            {/* Related Posts Section */}
            <div className="row mt-5">
              <div className="container">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="text-center mb-5"
                >
                  <h2 className="fw-bold" style={{ color: "#c69933" }}>
                    Related Blogs
                  </h2>
                  <div
                    className="divider mx-auto"
                    style={{
                      width: "80px",
                      height: "3px",
                      background: "#c69933",
                    }}
                  ></div>
                </motion.div>

                {relatedPosts.length > 0 ? (
                  <Slider {...settings} className="slick-slider">
                    {relatedPosts.map((blog) => (
                      <motion.div
                        key={blog.id}
                        className="p-3"
                        whileHover={{ y: -5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="card h-100 shadow-sm d-flex flex-column border-0 overflow-hidden">
                          <div className="position-relative overflow-hidden">
                            <img
                              src={blog?.thumbnail}
                              alt="Post Thumbnail"
                              className="card-img-top p-2 shadow-sm"
                              style={{
                                height: "15rem",
                                width: "100%",
                                objectFit: "cover",
                              }}
                              loading="lazy"
                            />
                            <div className="hover-overlay"></div>
                          </div>

                          <div className="card-body d-flex flex-column">
                            <h5
                              className="card-title fw-semibold"
                              style={{
                                minHeight: "3em",
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {blog.title}
                            </h5>
                            <div className="mt-auto pt-3">
                              <div style={{ marginTop: "auto" }}>
                                <div
                                  onClick={() => handleViewDetails(blog)}
                                  className="cs_post_btn"
                                  style={{ cursor: "pointer" }}
                                >
                                  <span>Read More</span>
                                  <span>
                                    <i className="fa-solid fa-angle-right"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </Slider>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">No related posts found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="cs_height_120 cs_height_lg_80"></div>
        </section>
      </div>
    </>
  );
};

export default BlogDetails;
